/* ==========================================================================
   Requipco — brand interactions
   Progressive enhancement only: every section renders and works without JS.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------------- */
  function initReveal(root) {
    var targets = (root || document).querySelectorAll('.sbd-reveal:not([data-sbd-observed])');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
        el.setAttribute('data-sbd-observed', '');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    targets.forEach(function (el) {
      el.setAttribute('data-sbd-observed', '');
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Delivery cut-off countdown
     Counts down to the configured local cut-off hour on business days.
     --------------------------------------------------------------------- */
  function initCutoff(root) {
    var nodes = (root || document).querySelectorAll('[data-sbd-cutoff]:not([data-sbd-started])');

    nodes.forEach(function (node) {
      node.setAttribute('data-sbd-started', '');

      var hour = parseInt(node.getAttribute('data-cutoff-hour'), 10);
      if (isNaN(hour)) hour = 21;

      var clock = node.querySelector('[data-sbd-cutoff-clock]');
      var openText = node.getAttribute('data-text-open') || '';
      var closedText = node.getAttribute('data-text-closed') || '';
      var label = node.querySelector('[data-sbd-cutoff-label]');

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        var now = new Date();
        var cutoff = new Date(now);
        cutoff.setHours(hour, 0, 0, 0);

        var day = now.getDay(); // 0 = Sunday, 6 = Saturday
        var isWeekend = day === 0 || day === 6;
        var diff = cutoff.getTime() - now.getTime();

        if (isWeekend || diff <= 0) {
          node.classList.add('sbd-cutoff--closed');
          if (label && closedText) label.textContent = closedText;
          if (clock) clock.textContent = '';
          return;
        }

        node.classList.remove('sbd-cutoff--closed');
        if (label && openText) label.textContent = openText;

        var totalSeconds = Math.floor(diff / 1000);
        var h = Math.floor(totalSeconds / 3600);
        var m = Math.floor((totalSeconds % 3600) / 60);
        var s = totalSeconds % 60;

        if (clock) clock.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
      }

      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------------------------
     Product finder
     A small decision tree. Each option carries the next step id, or a
     destination url on the final step.
     --------------------------------------------------------------------- */
  function initFinder(root) {
    var finders = (root || document).querySelectorAll('[data-sbd-finder]:not([data-sbd-started])');

    finders.forEach(function (finder) {
      finder.setAttribute('data-sbd-started', '');

      var steps = Array.prototype.slice.call(finder.querySelectorAll('[data-finder-step]'));
      var pips = Array.prototype.slice.call(finder.querySelectorAll('[data-finder-pip]'));
      var backBtn = finder.querySelector('[data-finder-back]');
      var stepLabel = finder.getAttribute('data-step-label') || '';
      var history = [];

      if (!steps.length) return;

      function show(id) {
        var active = null;
        steps.forEach(function (step) {
          var match = step.getAttribute('data-finder-step') === id;
          step.classList.toggle('is-active', match);
          if (match) active = step;
        });

        // Progress reflects how deep the visitor actually is, not which step
        // element happens to sit where in the DOM — the tree branches, so the
        // two are not the same.
        var isResult = id === 'result';
        pips.forEach(function (pip, i) {
          pip.classList.toggle('is-done', isResult || i <= history.length);
        });

        if (active) {
          var num = active.querySelector('[data-finder-num]');
          if (num) num.textContent = (stepLabel + ' ' + (history.length + 1)).trim();
        }

        if (backBtn) backBtn.hidden = history.length === 0;
      }

      finder.addEventListener('click', function (event) {
        var opt = event.target.closest('[data-finder-next]');
        if (opt && finder.contains(opt)) {
          // Resolving options are real links so the flow degrades without JS.
          // With JS we intercept and show the recommendation panel instead.
          if (opt.tagName === 'A') event.preventDefault();

          var current = opt.closest('[data-finder-step]');
          if (current) history.push(current.getAttribute('data-finder-step'));

          var result = opt.getAttribute('data-finder-result');
          var next = opt.getAttribute('data-finder-next');

          // Carry the chosen answer into the result panel so the
          // recommendation reads as a genuine response, not a generic one.
          if (result) {
            var resultStep = finder.querySelector('[data-finder-step="' + next + '"]');
            if (resultStep) {
              var titleEl = resultStep.querySelector('[data-finder-result-title]');
              var bodyEl = resultStep.querySelector('[data-finder-result-body]');
              var linkEl = resultStep.querySelector('[data-finder-result-link]');
              var title = opt.getAttribute('data-result-title');
              var body = opt.getAttribute('data-result-body');
              var url = opt.getAttribute('data-result-url');
              if (titleEl && title) titleEl.textContent = title;
              if (bodyEl && body) bodyEl.textContent = body;
              if (linkEl && url) linkEl.setAttribute('href', url);
            }
          }

          show(next);
          return;
        }

        if (event.target.closest('[data-finder-back]')) {
          var prev = history.pop();
          if (prev) show(prev);
        }
      });

      show(steps[0].getAttribute('data-finder-step'));
    });
  }

  /* ---------------------------------------------------------------------
     Sticky bar shadow
     --------------------------------------------------------------------- */
  function initStickyBar(root) {
    var bars = (root || document).querySelectorAll('.sbd-stickybar:not([data-sbd-started])');
    if (!bars.length || !('IntersectionObserver' in window)) return;

    bars.forEach(function (bar) {
      bar.setAttribute('data-sbd-started', '');

      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;height:1px;width:1px;';
      bar.parentNode.insertBefore(sentinel, bar);

      new IntersectionObserver(
        function (entries) {
          bar.classList.toggle('is-stuck', !entries[0].isIntersecting);
        },
        { rootMargin: '-1px 0px 0px 0px', threshold: 0 }
      ).observe(sentinel);
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot(root) {
    initReveal(root);
    initCutoff(root);
    initFinder(root);
    initStickyBar(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // Re-run after theme editor section reloads so the editor preview stays live.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
